import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, TextField
} from '@mui/material'
import { useSmartTranslation } from '../../hooks/useSmartTranslation'
import { apiGet } from '../../Api_Functions'
import { outcomeOf, printBatch } from './labelPrintingApi'
import { OUTCOME_TEXT, textOf } from './labelPrintingTexts'
import useActiveLabelPrinters from './useActiveLabelPrinters'
import QrPrinterSelect from './QrPrinterSelect'

const idle = { kind: '', text: '', missing: [] }

// Historia 226, enmienda E9 (S203, S204): print the labels of all the parts of a fabrication order in one request. The
// printer (the user's default one comes chosen), the copies per part (E7) and the CURRENT Part template (its latest
// non-archived version) are asked here; Sun prints in order, stops at the first printer failure and says how far it got,
// and "Retry the missing ones" asks again with only the pending ids. A text cut is asked once for the whole batch.
// `partIds()` resolves the ids of the order's parts; `columns` are the translated {field, name} titles of a Part label.
const PartsBatchPrintDialog = ({ open, onClose, partIds, columns }) => {
  const { t_ } = useSmartTranslation()
  const printers = useActiveLabelPrinters(open)
  const [copies, setCopies] = useState('1')
  const [includeTimestamp, setIncludeTimestamp] = useState(false) // E14
  const [template, setTemplate] = useState({})
  const [ids, setIds] = useState([])
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(idle)
  const [asking, setAsking] = useState({ open: false, count: 0, request: {} })
  const copiesValid = /^\d{1,2}$/.test(copies) && Number(copies) >= 1

  useEffect(() => {
    if (!open) return
    const load = async () => {
      const [versions, parts] = await Promise.all([apiGet('qrTemplates/Part').catch(() => []), partIds().catch(() => [])])
      setTemplate(versions[versions.length - 1] || {})
      setIds(parts)
    }
    load()
  }, [open, partIds])

  const fieldLabels = () => (template.selectedFields || []).reduce((labels, field) => {
    const column = columns.find((candidate) => candidate.field === field)
    return column ? { ...labels, [field]: column.name } : labels
  }, {})

  const send = async (request) => {
    setBusy(true)
    setResult(idle)
    try {
      const response = await printBatch(request)
      if (response.failedAt) {
        const missing = request.entityIds.slice(request.entityIds.indexOf(response.failedAt))
        setResult({
          kind: 'error',
          text: t_('{{printed}} of {{total}} printed · {{reason}}', {
            printed: response.printed,
            total: response.total,
            reason: t_(textOf(OUTCOME_TEXT, response.outcome))
          }),
          missing
        })
      } else if (response.printed < response.total) {
        // Sun skips a part that does not exist or whose QR does not fit and goes on (nothing to retry): say how many
        // came out and why the first skipped one did not.
        const skipped = (response.items || []).find((item) => item.outcome !== 'PRINTED') || {}
        setResult({
          kind: 'warning',
          text: t_('{{printed}} of {{total}} printed · {{reason}}', {
            printed: response.printed,
            total: response.total,
            reason: t_(skipped.outcome === 'QR_DOES_NOT_FIT' ? 'The QR does not fit on this label' : textOf(OUTCOME_TEXT, skipped.outcome))
          }),
          missing: []
        })
      } else {
        setResult({ kind: 'success', text: t_('{{count}} labels printed', { count: response.printed }), missing: [] })
      }
    } catch (error) {
      if (outcomeOf(error) === 'TEXT_TRUNCATION_NOT_CONFIRMED') {
        setAsking({ open: true, count: error.data.linesDropped, request })
      } else {
        setResult({ kind: 'error', text: t_(textOf(OUTCOME_TEXT, outcomeOf(error)) || 'Unexpected error'), missing: [] })
      }
    } finally {
      setBusy(false)
    }
  }

  const buildRequest = (entityIds) => ({
    entityType: 'Part',
    entityIds,
    printerId: printers.printerId,
    templateId: template.id,
    copies: Number(copies),
    acceptTextTruncation: false,
    includeTimestamp,
    fieldLabels: fieldLabels()
  })

  const handlePrint = () => send(buildRequest(ids))
  const handleRetry = () => send(buildRequest(result.missing))
  const handleCopiesChange = (event) => setCopies(event.target.value)
  const handleTimestampChange = (event) => setIncludeTimestamp(event.target.checked)
  const handlePrinterChange = (printerId) => printers.choose(printerId)
  const handleMakeDefault = (printerId) => printers.makeDefault(printerId)
  const handleConfirmCut = () => {
    const { request } = asking
    setAsking({ open: false, count: 0, request: {} })
    send({ ...request, acceptTextTruncation: true })
  }
  const handleCancelCut = () => setAsking({ open: false, count: 0, request: {} })

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{t_('Print the labels of the parts')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', pt: 1, flexWrap: 'wrap' }}>
          <QrPrinterSelect
            printers={printers.printers}
            printerId={printers.printerId}
            defaultId={printers.defaultId}
            onChange={handlePrinterChange}
            onMakeDefault={handleMakeDefault}
          />
          <TextField
            label={t_('Copies')}
            size='small'
            value={copies}
            onChange={handleCopiesChange}
            error={!copiesValid}
            helperText={copiesValid ? '' : t_('Between 1 and 99')}
            inputProps={{ inputMode: 'numeric', maxLength: 3 }}
            sx={{ width: 110 }}
          />
        </Box>
        <FormControlLabel
          control={<Checkbox checked={includeTimestamp} onChange={handleTimestampChange} size='small' />}
          label={t_('Include date and time')}
        />
        {result.text && (
          <Alert
            severity={result.kind}
            sx={{ mt: 2 }}
            action={result.missing.length > 0
              ? <Button color='inherit' size='small' disabled={busy} onClick={handleRetry}>{t_('Retry the missing ones')}</Button>
              : undefined}
          >
            {result.text}
          </Alert>
        )}
        {asking.open && (
          <Alert
            severity='warning'
            sx={{ mt: 2 }}
            action={
              <>
                <Button color='inherit' size='small' onClick={handleCancelCut}>{t_('Cancel')}</Button>
                <Button color='inherit' size='small' onClick={handleConfirmCut}>{t_('Print anyway')}</Button>
              </>
            }
          >
            {t_('Not all the text fits on the label of {{printer}}: {{count}} lines will be cut.', {
              printer: printers.chosen.name || '',
              count: asking.count
            })}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t_('Cancel')}</Button>
        <Button
          variant='contained'
          onClick={handlePrint}
          disabled={printers.printerId === '' || !copiesValid || busy || ids.length === 0 || !template.id}
          startIcon={busy ? <CircularProgress size={18} color='inherit' /> : undefined}
        >
          {busy ? t_('Printing…') : t_('Print')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PartsBatchPrintDialog
